/**
 * SeedFromNotes — Phase 5b free-text → LLM extraction → preview → commit.
 *
 * Per user direction (Option A): pure paste textbox, NO pre-fill from the
 * reported MATCH_PERSPECTIVE. Extracted claims are flagged as user
 * hypotheses (evidence_type='user_freetext_seed') and land as
 * pending_confirmation so the Phase 3 flow gates activation.
 */
import React, { useState } from "react";
import { Loader2, AlertCircle, Sparkles, Check, X } from "lucide-react";
import { authAxios } from "../../utils/auth";

const MAX_CHARS = 6000;

export default function SeedFromNotes({ matchId, onCommit, coachSessionId = null }) {
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("write"); // 'write' | 'extracting' | 'preview' | 'committing' | 'done'
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState(null);

  async function extract() {
    setError(null);
    setPhase("extracting");
    try {
      const res = await authAxios.post(
        `/relationship/${matchId}/perspective/seed-from-freetext`,
        { free_text: text, coach_session_id: coachSessionId || null }
      );
      const data = res.data;
      setPreview(data);
      // Default: all selected
      setSelected(new Set(data.claims.map((_, i) => i)));
      setPhase("preview");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setError(detail || `Extract failed${status ? ` (${status})` : ""}`);
      setPhase("write");
    }
  }

  async function commit() {
    if (!preview) return;
    setError(null);
    setPhase("committing");
    try {
      const indices = Array.from(selected).sort((a, b) => a - b);
      const res = await authAxios.post(
        `/relationship/${matchId}/perspective/seed-from-freetext/commit`,
        {
          preview_id: preview.preview_id,
          accepted_indices: indices,
        }
      );
      setPhase("done");
      onCommit && onCommit(res.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setError(detail || `Commit failed${status ? ` (${status})` : ""}`);
      setPhase("preview");
    }
  }

  function reset() {
    setText("");
    setPreview(null);
    setSelected(new Set());
    setError(null);
    setPhase("write");
  }

  function toggle(idx) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  return (
    <details
      className="border border-divine-gold/15 rounded-md bg-divine-black/40"
      data-testid="seed-from-notes-section"
    >
      <summary className="cursor-pointer px-3 py-2 text-divine-gold/80 text-xs uppercase tracking-wide flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5" />
        Seed claims from your notes
      </summary>
      <div className="p-3 space-y-3">
        {error ? (
          <div
            className="flex items-start gap-2 p-2 text-rose-300 text-xs border border-rose-500/30 rounded"
            data-testid="seed-error"
          >
            <AlertCircle className="w-3 h-3 mt-0.5" />
            <span>{error}</span>
          </div>
        ) : null}

        {phase === "write" ? (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
              rows={6}
              placeholder="Paste your own private notes / observations / hypotheses about this person. Eros will structure them as user-hypothesis claims for you to confirm later."
              className="w-full bg-black/50 border border-divine-gold/20 rounded p-2 text-white text-xs"
              data-testid="seed-textarea"
            />
            <div className="flex items-center justify-between">
              <span className="text-white/30 text-[10px]">
                {text.length} / {MAX_CHARS} chars
              </span>
              <button
                onClick={extract}
                disabled={!text.trim()}
                className="bg-divine-gold/20 hover:bg-divine-gold/30 text-divine-gold text-xs px-3 py-1 rounded disabled:opacity-30"
                data-testid="seed-extract-btn"
              >
                Extract claims
              </button>
            </div>
          </>
        ) : null}

        {phase === "extracting" ? (
          <div className="flex items-center gap-2 text-white/60 text-xs">
            <Loader2 className="w-3 h-3 animate-spin" />
            Extracting claims (this may take ~5-15 seconds)…
          </div>
        ) : null}

        {phase === "preview" && preview ? (
          <div className="space-y-2">
            <div className="text-white/60 text-xs">
              Found {preview.claims.length} claim{preview.claims.length === 1 ? "" : "s"}.
              Untick any you don't want to keep. Selected claims are added to your perspective.
            </div>
            <ul className="space-y-1.5 max-h-64 overflow-y-auto">
              {preview.claims.map((c, idx) => (
                <li
                  key={idx}
                  data-testid={`seed-claim-${idx}`}
                  className={`border rounded p-2 text-xs cursor-pointer ${
                    selected.has(idx)
                      ? "border-divine-gold/40 bg-divine-gold/5"
                      : "border-white/10 bg-divine-black/30 opacity-50"
                  }`}
                  onClick={() => toggle(idx)}
                >
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5">
                      {selected.has(idx) ? (
                        <Check className="w-3 h-3 text-divine-gold" />
                      ) : (
                        <X className="w-3 h-3 text-white/30" />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-divine-gold/80 text-[10px] uppercase tracking-wide">
                        {c.domain} · {c.topic} · {c.confidence}
                      </div>
                      <div className="text-white/85">{c.claim_text}</div>
                      {c.evidence_summary ? (
                        <div className="text-white/40 italic mt-0.5">
                          {c.evidence_summary}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between">
              <button
                onClick={reset}
                className="text-white/50 hover:text-white text-xs"
                data-testid="seed-reset-btn"
              >
                Start over
              </button>
              <button
                onClick={commit}
                disabled={selected.size === 0}
                className="bg-divine-gold/30 hover:bg-divine-gold/40 text-divine-gold text-xs px-3 py-1 rounded disabled:opacity-30"
                data-testid="seed-commit-btn"
              >
                Add {selected.size} to perspective
              </button>
            </div>
          </div>
        ) : null}

        {phase === "committing" ? (
          <div className="flex items-center gap-2 text-white/60 text-xs">
            <Loader2 className="w-3 h-3 animate-spin" />
            Adding claims…
          </div>
        ) : null}

        {phase === "done" ? (
          <div
            className="text-emerald-300 text-xs flex items-center justify-between"
            data-testid="seed-done"
          >
            <span>✓ Added. They now appear in your perspective above. Edit, lock, or retract any of them anytime.</span>
            <button
              onClick={reset}
              className="text-white/50 hover:text-white text-xs"
            >
              Add more
            </button>
          </div>
        ) : null}
      </div>
    </details>
  );
}
